/**
 * ErrorBoundary — wraps the whole app so a runtime exception in any child
 * never results in a totally blank white screen (the worst possible UX,
 * especially inside the Android WebView where the user has no devtools).
 *
 * On error we:
 *   1. Log to console (visible via Chrome remote-debugging if user attaches USB).
 *   2. POST a lightweight crash report to /api/errors/ui (best-effort).
 *   3. Show a friendly Urdu/Hinglish fallback with a "Reload" button.
 */
import React from "react";

export default class ErrorBoundary extends React.Component {
  state = { error: null, info: null };

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error("[ErrorBoundary]", error, info);
    this.setState({ info });
    // Best-effort report — never block render.
    try {
      const url = `${process.env.REACT_APP_BACKEND_URL || ""}/api/errors/ui`;
      fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          message: String(error?.message || error),
          stack: String(error?.stack || "").slice(0, 4000),
          component_stack: String(info?.componentStack || "").slice(0, 4000),
          url: typeof window !== "undefined" ? window.location.href : "",
          ua: typeof navigator !== "undefined" ? navigator.userAgent : "",
        }),
        keepalive: true,
      }).catch(() => {/* swallow */});
    } catch {/* swallow */}
  }

  reload = () => {
    try {
      // Hard reload so any half-loaded module is reset.
      window.location.reload();
    } catch {/* ignore */}
  };

  goHome = () => {
    try {
      window.location.hash = "#/login";
      window.location.reload();
    } catch {/* ignore */}
  };

  render() {
    if (!this.state.error) return this.props.children;
    return (
      <div
        data-testid="error-boundary"
        className="min-h-screen flex flex-col items-center justify-center bg-slate-50 p-6 text-center"
      >
        <div className="max-w-md w-full bg-white rounded-lg shadow-md border border-rose-100 p-6">
          <div className="w-12 h-12 mx-auto rounded-full bg-rose-100 flex items-center justify-center text-rose-600 text-2xl font-bold">!</div>
          <h1 className="font-display text-2xl font-bold text-slate-900 mt-4">Kuch masla ho gaya</h1>
          <p className="text-sm text-slate-600 mt-2">
            App mein ek unexpected error aaya hai. Aap dobara try karein,
            agar phir bhi nahi chale to support se rabta karein:
            <a className="text-emerald-700 font-semibold ml-1" href="mailto:support@myhisaabpro.com">support@myhisaabpro.com</a>
          </p>
          <div className="mt-5 flex gap-2 justify-center">
            <button
              type="button"
              onClick={this.reload}
              className="px-4 py-2 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white font-semibold text-sm"
              data-testid="error-reload-btn"
            >
              Reload karein
            </button>
            <button
              type="button"
              onClick={this.goHome}
              className="px-4 py-2 rounded-md bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold text-sm"
              data-testid="error-login-btn"
            >
              Login pe wapas
            </button>
          </div>
          {/* Developer details — collapsed for end-users, expanded on long press. */}
          <details className="mt-4 text-left">
            <summary className="text-xs text-slate-400 cursor-pointer">Technical details</summary>
            <pre className="mt-2 text-[10px] text-rose-700 bg-rose-50 p-2 rounded overflow-auto max-h-48">{String(this.state.error?.message || this.state.error)}{"\n\n"}{String(this.state.error?.stack || "").slice(0, 1200)}</pre>
          </details>
        </div>
      </div>
    );
  }
}
